import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { SIGNOL_COLOUR_NAMES } from "@signolteam/shared/colours";
import AppUtils from "@src/utils/appUtils";
import HorizontalLayout from "@src/components/cards/HorizontalLayout";
import Button from "@src/components/atoms/Button";
import Checkbox from "@src/components/atoms/Checkbox";
import { useIsMobileWidth } from "@src/utils/mediaQuery";
import VerticalLayout from "@src/components/cards/VerticalLayout";
import { useDCContext } from "@src/context/dcContext";

const FeedbackForm = ({
  existingComment = null,
  onSubmit,
  color = "neutral",
  preferredEventName = "opportunity",
}) => {
  const [comment, setComment] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMobile = useIsMobileWidth();
  const minLength = 2;
  const maxLength = 1000;
  const dc = useDCContext();
  const { getDynamicReplacement } = dc;

  const [isVisible, setIsVisible] = useState(false);

  const observerRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.intersectionRatio === 1);
      },
      {
        threshold: [0, 1], // 0: partially visible, 1: fully visible
      },
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  if (!!existingComment) {
    return (
      <div className={`p-4 space-y-4 bg-${color}-200 `}>
        <div>
          <div className="text-lg font-bold">
            You shared this comment with us:
          </div>

          <div className="mt-4 p-3 ">
            <p className="text-gray-800">{existingComment}</p>
          </div>
        </div>
      </div>
    );
  }

  const placeHolderText = getDynamicReplacement(
    "captain__events__behaviour__feedback__placeholder",
    { EVENT_NAME: preferredEventName },
    "",
    true,
  );

  const handleCommentChange = (e) => {
    const value = e.target.value;
    setComment(value);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);
    try {
      // Validation
      if (!agreed) {
        setError("You must agree to the terms to submit a comment");
        return;
      }

      if (comment.length < minLength) {
        setError(`Comment must be at least ${minLength} characters`);
        return;
      }

      if (comment.length > maxLength) {
        setError(`Comment must be less than ${maxLength} characters`);
        return;
      }

      await onSubmit(comment);
      setSubmitted(true);
    } catch (err) {
      setError("Failed to submit comment. Please try again.");
      console.error("Submission error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`bg-${color}-200 `}>
      {!!submitted && (
        <div className="p-4 text-center">
          <p className="text-lg font-medium">
            {getDynamicReplacement(
              "captain__events__behaviour__feedback__thanks_message",
            )}
          </p>
        </div>
      )}
      {!submitted && (
        <div
          ref={observerRef}
          className={`p-4 space-y-4 transition-colors duration-300 ${
            isVisible ? `bg-${color}-200` : `bg-${color}-100`
          }`}
        >
          <div className="text-lg font-bold">
            {getDynamicReplacement(
              "captain__events__behaviour__feedback__comment",
            )}
          </div>

          <form onSubmit={handleSubmit} className="mt-4">
            <textarea
              value={comment}
              onChange={handleCommentChange}
              className={`data-hj-allow bg-white text-sm text-area mx-auto p-2 mb-1 w-full rounded-md min-h-36 md:min-h-16 ${error ? " text-red-500 bg-red-200" : ""}`}
              rows={4}
              placeholder={placeHolderText}
              maxLength={maxLength}
            />
            {error && (
              <div className="mt-4 text-xs italic text-red-500">{error}</div>
            )}

            <div
              className={`${isMobile ? "flex flex-col justify-center items-stretch p-2" : "grid grid-cols-2 p-2"} gap-4`}
            >
              <Checkbox
                id="agreement"
                name="agreement"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                fontSize={"xs"}
                type={"italic"}
                label={getDynamicReplacement(
                  "captain__events__behaviour__feedback__check",
                )}
                maxWidthMd={!isMobile}
              />

              <Button
                submit={true}
                disabled={!comment || !agreed}
                loading={isSubmitting}
                className="px-4"
              >
                Submit
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export const feedbackFormControls = {
  color: {
    propType: PropTypes.string,
    description: "Background color",
    controlType: "select",
    options: SIGNOL_COLOUR_NAMES,
  },
  existingComment: {
    propType: PropTypes.string,
    description: "Existing comment that the user submitted in the past",
    controlType: "text",
  },
  preferredEventName: {
    propType: PropTypes.string,
    description: "Preferred event name",
    controlType: "select",
    options: ["opportunity", "day", "event"],
  },
  onSubmit: {
    propType: PropTypes.func,
    description: "Submit function to save the comment",
    disable: true,
  },
};

FeedbackForm.propTypes = AppUtils.getPropTypeFromControls(feedbackFormControls);

export default FeedbackForm;
